import { Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpResponse,
    HttpStatusCode,
} from '@angular/common/http';
import { Observable, Observer, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TokenService } from './viewbid/shared/services/token.service';
import { TokenStorageService, TokenType, JWTTokens } from 'viewbid-shared-lib';
import {
    AUTH0_PROTECTED_ENDPOINTS,
    CRITICAL_ERROR_TYPES,
    NO_AUTH_ENDPOINTS,
    PROTECTED_API_ENDPOINTS,
} from './viewbid/shared/contants';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
    private readonly retryCount = new Map<string, number>();

    constructor(
        private readonly tokenService: TokenService,
        private readonly tokenStorageService: TokenStorageService,
    ) {}

    private requiresAuth0Token(url: string): boolean {
        // making sure getAuth0Token is not called in case of impersonating
        if (
            this.tokenStorageService.isMasquerading() &&
            window.name === 'masquerade'
        ) {
            return false;
        }
        return AUTH0_PROTECTED_ENDPOINTS.some((endpoint: string): boolean =>
            url.includes(endpoint),
        );
    }

    private preventAnonymousApiCall(url: string): boolean {
        return (
            this.requiresAppToken(url) && !this.tokenStorageService.getToken()
        );
    }

    private requiresAppToken(url: string): boolean {
        return PROTECTED_API_ENDPOINTS.some((endpoint: string): boolean =>
            url.includes(endpoint),
        );
    }

    private requiresNoAuth(url: string): boolean {
        return NO_AUTH_ENDPOINTS.some((endpoint: string): boolean =>
            url.includes(endpoint),
        );
    }

    private addAuthHeader(request: HttpRequest<any>): HttpRequest<any> {
        if (this.requiresNoAuth(request.url)) {
            return request;
        }

        let token: string | null = null;
        if (this.requiresAuth0Token(request.url)) {
            token = this.tokenStorageService.getAuth0Token();
        } else {
            // Guard: if masquerading but this is NOT the masquerade popup window,
            // we must use the regular access token to not break existing tabs.
            if (
                this.tokenStorageService.isMasquerading() &&
                window.name !== 'masquerade'
            ) {
                token = this.tokenStorageService.getLocalStorageItem(
                    TokenType.ACCESS,
                );
            } else {
                token = this.tokenStorageService.getToken();
            }
        }

        if (request.url.includes('cloudfront.net') || !token) {
            return request;
        }

        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`,
            },
        });
    }

    private handleMasqueradeError(error: any): void {
        if (
            error.error === 'masquerade_session_expired' ||
            error.status === 401 ||
            (error.status === 404 && !this.tokenStorageService.isMasquerading())
        ) {
            // Avoid letting background tabs clear masquerade tokens
            if (window.name !== 'masquerade') {
                return;
            }
            // Clean up masquerade state and tokens
            this.tokenStorageService.stopMasquerade();
            // Clear any stale masquerade data from localStorage
            window.localStorage.removeItem(TokenType.MASQUERADE_ACCESS);
            window.localStorage.removeItem(TokenType.ORIGINAL_ACCESS);
            window.localStorage.removeItem(TokenType.MASQUERADE_STATE_KEY);
        }
    }

    private shouldAttemptTokenRefresh(
        error: any,
        request: HttpRequest<any>,
    ): boolean {
        // Don't attempt refresh if:
        // 1. Request is to a no-auth endpoint
        // 2. Error is not 401
        // 3. Error indicates a critical auth failure
        // 4. Currently masquerading (masquerade tokens can't be refreshed)
        if (
            this.requiresNoAuth(request.url) ||
            error.status !== HttpStatusCode.Unauthorized ||
            (error.error && CRITICAL_ERROR_TYPES.includes(error.error)) ||
            this.tokenStorageService.isMasquerading()
        ) {
            return false;
        }

        return true;
    }

    private handleTokenUpdate(event: HttpResponse<any>): void {
        const refreshToken: string | null = event.headers.get('refresh-token');
        const accessToken: string | null = event.headers.get('access-token');

        if (refreshToken || accessToken) {
            // Guard: if a masquerade session is active but we are in the admin
            // (parent) window, do NOT write tokens — the response may originate
            // from the bidder popup and would overwrite the admin's own token.
            if (
                this.tokenStorageService.isMasquerading() &&
                window.name !== 'masquerade'
            ) {
                return;
            }
            this.tokenStorageService.storeJWTToken({
                refreshToken,
                accessToken,
            });
        }
    }

    private retryRequestWithNewToken(
        request: HttpRequest<any>,
        next: HttpHandler,
        requestId: string,
        observer: Observer<HttpEvent<any>>,
        originalError: any,
    ): void {
        if (this.requiresAuth0Token(request.url)) {
            this.tokenService.getAuth0AccessTokenSilently().subscribe({
                next: (newAuth0Token: string) => {
                    if (newAuth0Token) {
                        let newRequest: HttpRequest<any> = request.clone({
                            setHeaders: {
                                Authorization: `Bearer ${newAuth0Token}`,
                            },
                        });

                        // If it's a POST to sso-login and has a body, update the body too
                        if (
                            newRequest.body &&
                            typeof newRequest.body === 'object' &&
                            'token' in newRequest.body
                        ) {
                            newRequest = newRequest.clone({
                                body: {
                                    ...newRequest.body,
                                    token: newAuth0Token,
                                },
                            });
                        }

                        next.handle(newRequest).subscribe({
                            next: (event: HttpEvent<any>) =>
                                observer.next(event),
                            error: (error: any) => {
                                this.retryCount.delete(requestId);
                                observer.error(error);
                            },
                            complete: () => {
                                this.retryCount.delete(requestId);
                                observer.complete();
                            },
                        });
                    } else {
                        this.retryCount.delete(requestId);
                        observer.error(originalError);
                    }
                },
                error: (error: any) => {
                    this.retryCount.delete(requestId);
                    this.tokenService.logout();
                    observer.error(error);
                },
            });
        } else {
            this.tokenStorageService
                .refreshToken()
                .then((tokens: JWTTokens) => {
                    if (tokens.accessToken) {
                        const newRequest: HttpRequest<any> = request.clone({
                            setHeaders: {
                                Authorization: `Bearer ${tokens.accessToken}`,
                            },
                        });

                        next.handle(newRequest).subscribe({
                            next: (event: HttpEvent<any>) =>
                                observer.next(event),
                            error: (error: any) => {
                                this.retryCount.delete(requestId);
                                observer.error(error);
                            },
                            complete: () => {
                                this.retryCount.delete(requestId);
                                observer.complete();
                            },
                        });
                    } else {
                        this.retryCount.delete(requestId);
                        observer.error(originalError);
                    }
                })
                .catch((error: any) => {
                    this.retryCount.delete(requestId);
                    this.tokenService.logout();
                    observer.error(error);
                });
        }
    }

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler,
    ): Observable<HttpEvent<any>> {
        if (
            !this.tokenStorageService.isMasquerading() &&
            this.preventAnonymousApiCall(request.url)
        ) {
            return throwError(() => 'Preventing anonymous api call');
        }
        request = this.addAuthHeader(request);

        return next.handle(request).pipe(
            map((event: HttpEvent<any>) => {
                if (event instanceof HttpResponse) {
                    this.handleTokenUpdate(event);
                }
                return event;
            }),
            catchError((error: any) => {
                // Get or initialize retry count for this request URL
                const requestId: string = request.url;
                const currentRetryCount: number =
                    this.retryCount.get(requestId) ?? 0;

                // Check for masquerade errors
                if (
                    (this.tokenStorageService.isMasquerading() &&
                        window.name === 'masquerade') ||
                    error.status === 404
                ) {
                    this.handleMasqueradeError(error);
                    return throwError(() => error);
                }

                if (
                    this.shouldAttemptTokenRefresh(error, request) &&
                    currentRetryCount < 2
                ) {
                    // Increment retry count
                    this.retryCount.set(requestId, currentRetryCount + 1);

                    // Return an Observable that will perform the retry
                    return new Observable<HttpEvent<any>>(
                        (observer: Observer<HttpEvent<any>>) => {
                            this.retryRequestWithNewToken(
                                request,
                                next,
                                requestId,
                                observer,
                                error,
                            );
                        },
                    );
                }

                // Clean up retry count if we're not retrying
                this.retryCount.delete(requestId);

                // Handle critical errors that require logout
                if (
                    currentRetryCount >= 2 ||
                    (error.error && CRITICAL_ERROR_TYPES.includes(error.error))
                ) {
                    this.tokenService.logout();
                }

                // Handle 403-forbidden exception in scenario where the session is left idle
                if (error.status && error.status === 403) {
                    this.tokenService.logout();
                }

                return throwError(() => error);
            }),
        );
    }
}
